import React, { useEffect, useMemo } from 'react';
import { IBaseDisbursementContainerProps } from '../IBaseDisbursementContainerProps';
import { NumberField, SelectField, TextInput } from 'ui-toolkit';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { getDisabledLegacyInternalTransferCategories } from 'utilities/order/escrow/getDisabledLegacyInternalTransferCategories';
import { getFilteredLegacyInternalTransferCategories } from 'utilities/order/escrow/getFilteredLegacyInternalTransferCategories';
import { legacyInternalTransferCategoriesToOptions } from 'types/accounting/response/eInternalTransferCategory';
import { useGetDestinationAccountQuery } from 'API/order/escrow/InternalTransferDisbursementAPI';
import { IInternalTransferDisbursementFormData } from 'types/order/escrow/internalTransferDisbursements/IInternalTransferDisbursementFormData';
import { Field, FieldProps, useField } from 'formik';
import { SendInternalTransferButton } from './SendInternalTransferButton';
import { HoldbackContainer } from '../holdbacks/HoldbackContainer';
import { VoidInternalTransfersButton } from '../Voiding/VoidInternalTransfersButton';
import { DateFormat, getDateString } from '../../../../../../ui-toolkit/utilities/getDateString';
import { labelComparator } from '../../../../../../utilities/labelComparator';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';

export const InternalTransferDisbursementContainer: React.FC<IBaseDisbursementContainerProps> = ({
  disbursementType,
  TypeChange,
  isDirty,
}) => {
  const { data: order } = useSelectCurrentOrder();
  const [{ value: data }] = useField<IInternalTransferDisbursementFormData>('value');
  const [{ value: destinationAccountId }, , { setValue, setTouched }] = useField<string | undefined>(
    `value.destinationAccountId`
  );

  const {
    data: autoDestination,
    isSuccess,
    isLoading,
  } = useGetDestinationAccountQuery(
    { category: data.category ?? '', state: order.property.state ?? '' },
    { skip: !data.category || !order.property.state }
  );

  useEffect(() => {
    if (isSuccess && autoDestination.id !== destinationAccountId) {
      setValue(autoDestination.id);
    }
    if (!isSuccess && !isLoading && destinationAccountId !== undefined) {
      setValue(undefined);
    }
  }, [autoDestination, destinationAccountId, setValue, setTouched, isSuccess, isLoading]);

  const isReadOnly = disbursementType.isReadOnly(data) || !disbursementType?.hasEditPermission;
  const isSpruceWithZillowEnabled = useHasFeatureFlag('pinecone.spruce-with-zillow-closing');

  const categoryOptions = useMemo(() => {
    return legacyInternalTransferCategoriesToOptions(
      getFilteredLegacyInternalTransferCategories(order.parties),
      getDisabledLegacyInternalTransferCategories(order.property.state)
    ).toSorted(labelComparator);
  }, [order.parties, order.property.state]);

  return (
    <div className={'my-4 flex basis-full flex-wrap gap-y-2'}>
      <NumberField
        name={`value.amountCents`}
        className={'basis-1/4 pr-2'}
        label="Amount"
        mode="currency"
        placeholder="123,456.78"
        valueIsCents
        disabled={isReadOnly}
        isRequired
      />
      <SelectField
        name={`value.category`}
        className={'basis-1/3 pr-2'}
        options={categoryOptions}
        label={'Category'}
        placeholder={'Select one...'}
        isDisabled={isReadOnly}
        isRequired
      />
      {TypeChange}
      <HoldbackContainer id={data.id} isReadOnly={isReadOnly} isDirty={isDirty} />
      <Field name={'value.destinationAccountId'}>
        {({ field }: FieldProps) => (
          <TextInput
            className={'basis-1/2'}
            {...field}
            value={field.value && (isSpruceWithZillowEnabled ? autoDestination?.legalName : autoDestination?.spruceName)}
            label={'Destination Account'}
            placeholder={'Select one...'}
            readOnly={isReadOnly}
            isRequired
            disabled
          />
        )}
      </Field>
      {data.enqueuedForDisbursementAt && (
        <div className={'basis-full'}>
          <span className={'font-semibold'}>Initiated Date:</span>{' '}
          {getDateString(DateFormat.short, data.enqueuedForDisbursementAt)}
        </div>
      )}
      {data.completedAt && (
        <div className={'basis-full'}>
          <span className={'font-semibold'}>Disbursed Date:</span> {getDateString(DateFormat.short, data.completedAt)}
        </div>
      )}
      {data.voidedAt && (
        <div className={'basis-full'}>
          <span className={'font-semibold'}>Voided Date:</span>{' '}
          {getDateString(DateFormat.shortDateTime, data.voidedAt, true)}
        </div>
      )}
      {data.voidReason && (
        <div className={'basis-full'}>
          <span className={'font-semibold'}>Voided Reason:</span> {data.voidReason}
          {data.voidReasonDescription && ` - ${data.voidReasonDescription}`}
        </div>
      )}
      <div className={'flex basis-full justify-end'}>
        {!data.isNew && !isReadOnly && (
          <SendInternalTransferButton
            baseName={'value'}
            internalTransfer={data}
            disabled={isDirty || isReadOnly || data.holdback}
          />
        )}
        <VoidInternalTransfersButton
          internalDisbursement={data}
          destinationAccountName={isSpruceWithZillowEnabled ? autoDestination?.legalName : autoDestination?.spruceName}
        />
      </div>
    </div>
  );
};
